/**
 * Presentation model for Brunel's persistent live-status surface.
 *
 * This is not a second Worker lifecycle. Different structured Brunel sources
 * (audit events, terminal actions, run/test results, planner events) have their
 * own state machines. The bar only needs a small common shape to render facts
 * those sources already recorded.
 */

export type LiveStatusSeverity =
  | 'info'
  | 'working'
  | 'success'
  | 'waiting'
  | 'approval'
  | 'warning'
  | 'error'
  | 'disconnected';

export interface LiveStatusWorkerRef {
  readonly id: string;
  readonly number?: number;
  readonly name?: string;
}

export interface LiveStatusEvent {
  readonly id: string;
  readonly at: string;
  readonly severity: LiveStatusSeverity;
  readonly message: string;
  readonly source?: string | null;
  readonly worker?: LiveStatusWorkerRef | null;
}

export const LIVE_STATUS_SEVERITY_LABELS: Readonly<Record<LiveStatusSeverity, string>> = {
  info: 'Info',
  working: 'Working',
  success: 'Success',
  waiting: 'Waiting',
  approval: 'Approval needed',
  warning: 'Warning',
  error: 'Error',
  disconnected: 'Disconnected',
};

const ANNOUNCED_SEVERITIES: readonly LiveStatusSeverity[] = [
  'approval',
  'warning',
  'error',
  'disconnected',
];

export function shouldAnnounceLiveStatus(severity: LiveStatusSeverity): boolean {
  return ANNOUNCED_SEVERITIES.includes(severity);
}

export function isLiveStatusAttention(severity: LiveStatusSeverity): boolean {
  return severity === 'approval'
    || severity === 'warning'
    || severity === 'error'
    || severity === 'disconnected';
}

export function visibleLiveStatusEvents(
  events: readonly LiveStatusEvent[],
  maxVisible = 4,
): readonly LiveStatusEvent[] {
  const limit = Number.isFinite(maxVisible) ? Math.max(1, Math.trunc(maxVisible)) : 4;
  return events.slice(0, limit);
}

export function liveStatusWorkerLabel(worker: LiveStatusWorkerRef): string {
  const number = Number.isFinite(worker.number) ? `Worker ${worker.number}` : 'Worker';
  const name = worker.name?.trim();
  return name ? `${number} · ${name}` : number;
}

export function liveStatusTimeLabel(at: string): string {
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return at;
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(parsed));
}
